import { useRef, useState } from "react";
import { ActionList, Button, Icon, Popover, Spinner, Text, Tooltip } from "@shopify/polaris";
import { ChevronDownIcon, DeleteIcon, InfoIcon, UploadIcon } from "@shopify/polaris-icons";
import { MdFiberManualRecord, MdStar, MdFavorite, MdThumbUp } from "react-icons/md";
import { FaCheck, FaArrowRight } from "react-icons/fa";
import type { IconType } from "react-icons";
import { useFormContext, useWatch } from "react-hook-form";
import styles from "./IconUploader.module.css";
import { postData } from "../../utilities/networkCalls";
import config from "../../config";

export const ICON_TYPE_OPTIONS: { label: string; value: string; Icon?: IconType }[] = [
    { label: "None", value: "none" },
    { label: "Checkmark", value: "checkmark", Icon: FaCheck },
    { label: "Dot", value: "dot", Icon: MdFiberManualRecord },
    { label: "Arrow", value: "arrow", Icon: FaArrowRight },
    { label: "Star", value: "star", Icon: MdStar },
    { label: "Heart", value: "heart", Icon: MdFavorite },
    { label: "Thumbs up", value: "thumbs_up", Icon: MdThumbUp },
    { label: "Custom", value: "custom" },
];

export type IconOption = { label: string; value: string; Preview?: React.ComponentType; tooltip?: string };

export type IconUploaderProps = {
    iconTypeName: string;
    iconUrlName: string;
    label?: string;
    sizeFieldName: string;
    options?: IconOption[];
    showPreviewInDropdown?: boolean;
    onCustomImageClick?: () => void;
};

const IconUploader = ({
    iconTypeName,
    iconUrlName,
    label = "Icon type",
    sizeFieldName,
    options,
    showPreviewInDropdown = true,
    onCustomImageClick,
}: IconUploaderProps) => {
    const { control, setValue } = useFormContext();
    const iconUrl = useWatch({ control, name: iconUrlName });
    const iconType = useWatch({ control, name: iconTypeName }) || "none";
    const size = useWatch({ control, name: sizeFieldName, disabled: !sizeFieldName });
    const iconInputRef = useRef<HTMLInputElement | null>(null);
    const [iconLoading, setIconLoading] = useState(false);
    const [iconError, setIconError] = useState("");
    const [popoverActive, setPopoverActive] = useState(false);

    const resolvedOptions: IconOption[] =
        options ||
        ICON_TYPE_OPTIONS.map((opt) => {
            const IconComp = opt.Icon;
            return {
                label: opt.label,
                value: opt.value,
                Preview: IconComp ? () => <IconComp size={size} /> : undefined,
            };
        });

    const selectedMeta = resolvedOptions.find((opt) => opt.value === iconType) || resolvedOptions[0];

    const updateIcon = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
        if (!allowedTypes.includes(file.type)) {
            setIconError("Invalid file type. Only JPG and PNG are allowed.");
            return;
        }

        if (file.size > 1024 * 1024) {
            setIconError("File too large. Max size is 1MB.");
            return;
        }

        setIconLoading(true);
        setIconError("");

        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await postData({
                url: `/${config.appName}/upload`,
                payload: formData,
            });

            if (!response?.data?.url) {
                throw new Error("Upload failed");
            }

            setValue(iconUrlName, response.data.url, { shouldDirty: true });
        } catch (err) {
            console.error("Upload failed", err);
            setIconError("Failed to upload image.");
        } finally {
            setIconLoading(false);
        }
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {label && (
                <Text as="p" variant="bodyMd" fontWeight="medium">
                    {label}
                </Text>
            )}
            <div style={{ display: "flex", gap: "10px", alignItems: "start" }}>
                <Popover
                    active={popoverActive}
                    onClose={() => setPopoverActive(false)}
                    activator={
                        <Button
                            onClick={() => setPopoverActive((prev) => !prev)}
                            size="large"
                            // @ts-expect-error ignore
                            children={
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span>{selectedMeta.label}</span>
                                        {selectedMeta.Preview && <selectedMeta.Preview />}
                                    </span>
                                    <Icon source={ChevronDownIcon} tone="subdued" />
                                </div>
                            }
                        />
                    }
                >
                    <ActionList
                        items={resolvedOptions.map((opt) => ({
                            content: opt.label,
                            active: opt.value === iconType,
                            suffix:
                                (showPreviewInDropdown && opt.Preview) || opt.tooltip ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        {showPreviewInDropdown && opt.Preview && <opt.Preview />}
                                        {opt.tooltip && (
                                            <Tooltip content={opt.tooltip} width="wide">
                                                <Icon source={InfoIcon} tone="subdued" />
                                            </Tooltip>
                                        )}
                                    </div>
                                ) : undefined,
                            onAction: () => {
                                setValue(iconTypeName, opt.value, { shouldDirty: true });
                                if (opt.value !== "custom") {
                                    setValue(iconUrlName, "", { shouldDirty: true });
                                }
                                setPopoverActive(false);
                            },
                        }))}
                    />
                </Popover>

                {iconType === "custom" && onCustomImageClick && (
                    <div style={{ display: "flex", alignItems: "start", gap: "10px" }}>
                        <Button onClick={onCustomImageClick} size="large">
                            {iconUrl ? "Edit image" : "Add image"}
                        </Button>

                        {iconUrl && (
                            <div className={`${styles.badgeIcon} ${styles.selectedBadgeIcon}`}>
                                <span
                                    className={styles.deleteBadge}
                                    onClick={() => setValue(iconUrlName, "", { shouldDirty: true })}
                                >
                                    <Icon source={DeleteIcon} tone={"critical"} />
                                </span>
                                <img
                                    src={iconUrl}
                                    alt={"icon"}
                                    style={{ objectFit: "contain", width: "auto", height: "30px" }}
                                />
                            </div>
                        )}
                    </div>
                )}

                {iconType === "custom" && !onCustomImageClick && (
                    <div style={{ display: "flex", alignItems: "start", gap: "10px" }}>
                        <Tooltip content={iconUrl ? "Upload another icon" : "Upload icon"}>
                            <Button
                                icon={iconLoading ? <Spinner size="small" /> : UploadIcon}
                                onClick={() => iconInputRef.current?.click()}
                                disabled={iconLoading}
                                size="large"
                            >
                                {iconUrl ? "Edit image" : "Upload image"}
                            </Button>
                        </Tooltip>

                        {iconUrl && (
                            <div className={`${styles.badgeIcon} ${styles.selectedBadgeIcon}`}>
                                <span
                                    className={styles.deleteBadge}
                                    onClick={() => setValue(iconUrlName, "", { shouldDirty: true })}
                                >
                                    <Icon source={DeleteIcon} tone={"critical"} />
                                </span>
                                <img
                                    src={iconUrl}
                                    alt={"icon"}
                                    style={{ objectFit: "contain", width: "auto", height: "30px" }}
                                />
                            </div>
                        )}

                        <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            ref={iconInputRef}
                            onChange={updateIcon}
                            style={{ display: "none" }}
                        />

                        {iconError && <p style={{ color: "red", marginLeft: "10px" }}>{iconError}</p>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default IconUploader;
